import {
  AuthApiTokenResponse,
  STORAGE_KEYS
} from "../types";
import { isJwtExpired } from "#/utils/jwt";
import { getCookieKey } from "#/lib/supabase/utils";
import { BrowserResultBody } from "#/lib/command-types/command-result";
import { sendBrowserResultAction } from "#next/pages/api/browser-result.action";
import { ApiKeySessionManager } from "#/lib/api-token-session";
import { isBrowserExtension } from "#/utils/environment";

/**
 * Saves authentication data to Chrome storage
 */
export const saveAuthData = async (authData: AuthApiTokenResponse["user"]): Promise<void> => {
  await chrome.storage.local.set({
    [STORAGE_KEYS.ACCESS_TOKEN]: authData.accessToken,
    [STORAGE_KEYS.USER_ID]: authData.id,
    [STORAGE_KEYS.JWT]: authData.jwt,
    // [STORAGE_KEYS.DOMAIN]: authData.domain,
    [STORAGE_KEYS.USER_EMAIL]: authData.email
  });
};

export const saveTabUsed = async (args: {
  tabId: number,
  tabHref: string
} | null): Promise<void> => {
  if (args?.tabId != null) {
    await chrome.storage.local.set({
      [STORAGE_KEYS.TAB_ACTIVE_ON]: String(args.tabId),
      [STORAGE_KEYS.TAB_ACTIVE_HREF]: String(args.tabHref)
    });
  } else {
    await chrome.storage.local.remove([
      STORAGE_KEYS.TAB_ACTIVE_ON,
      STORAGE_KEYS.TAB_ACTIVE_HREF
    ]);
  }
};

type StoredData = {
  [K in (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]]: string | undefined
};

export const getStoredData = async (): Promise<StoredData> => {
  let data = await chrome.storage.local.get([
    STORAGE_KEYS.ACCESS_TOKEN,
    STORAGE_KEYS.USER_ID,
    STORAGE_KEYS.JWT,
    STORAGE_KEYS.TAB_ACTIVE_ON,
    STORAGE_KEYS.TAB_ACTIVE_HREF,
    STORAGE_KEYS.API_TOKEN
  ]);
  if (!data[STORAGE_KEYS.API_TOKEN]) {
    return {
      [STORAGE_KEYS.ACCESS_TOKEN]: undefined,
      [STORAGE_KEYS.USER_ID]: undefined,
      [STORAGE_KEYS.JWT]: undefined,
      [STORAGE_KEYS.TAB_ACTIVE_ON]: undefined,
      [STORAGE_KEYS.TAB_ACTIVE_HREF]: undefined,
      [STORAGE_KEYS.API_TOKEN]: undefined,
      [STORAGE_KEYS.USER_EMAIL]: undefined
    };
  }
  return {
    [STORAGE_KEYS.ACCESS_TOKEN]: data[STORAGE_KEYS.ACCESS_TOKEN],
    [STORAGE_KEYS.USER_ID]: data[STORAGE_KEYS.USER_ID],
    [STORAGE_KEYS.JWT]: data[STORAGE_KEYS.JWT],
    [STORAGE_KEYS.TAB_ACTIVE_ON]: data[STORAGE_KEYS.TAB_ACTIVE_ON],
    [STORAGE_KEYS.TAB_ACTIVE_HREF]: data[STORAGE_KEYS.TAB_ACTIVE_HREF],
    [STORAGE_KEYS.API_TOKEN]: data[STORAGE_KEYS.API_TOKEN],
    [STORAGE_KEYS.USER_EMAIL]: data[STORAGE_KEYS.USER_EMAIL]
  };
};

export const getUserSession = async (
  apiToken?: string
) => {
  if (!isBrowserExtension()) {
    throw new Error("This function should only be called in the browser extension");
  }
  let _apiToken: string;
  if (!apiToken) {
    const data = await getStoredData();
    if (!data.mcp_api_token) {
      throw new Error("No API token found");
    }
    _apiToken = data.mcp_api_token;
  } else {
    _apiToken = apiToken;
  }
  const session = await ApiKeySessionManager.new(_apiToken);
  return session;
};
/**
 * Gets authentication data from Chrome storage
 */
export const getAuthData = async (
  refresh = false
): Promise<{
  accessToken?: string,
  userId?: string,
  jwt?: string,
  tabActiveOn?: string,
  tabActiveHref?: string,
  // domain?: string,
  apiToken?: string
}> => {
  let data = await getStoredData();
  if (!data.mcp_api_token) {
    return { accessToken: undefined, userId: undefined, jwt: undefined, tabActiveOn: undefined, tabActiveHref: undefined, apiToken: undefined };
  }
  if (refresh || isJwtExpired(data.mcp_access_token)) {
    // const session = await authenticateWithApiToken({ apiToken: data[STORAGE_KEYS.API_TOKEN] });
    const session = await getUserSession(data.mcp_api_token);
    await saveAuthData(session.user);
    data = await getStoredData();
  }

  return {
    accessToken: data.mcp_access_token,
    userId: data.mcp_user_id,
    jwt: data.mcp_jwt,
    tabActiveOn: data.mcp_tab_active_on,
    tabActiveHref: data.mcp_tab_active_href,
    apiToken: data.mcp_api_token
  };
};

/**
 * Sends browser result data to the API
 */
export const sendBrowserResult = async (
  result: BrowserResultBody
): Promise<void> => {
  const { apiToken, accessToken, userId, jwt } = await getAuthData();

  if (!apiToken || !accessToken) {
    throw new Error("Authentication data not found");
  }

  return sendBrowserResultAction(apiToken, result, {
    "Authorization": `Bearer ${accessToken}`,
    "user-id": userId || "",
    "Cookie": `${getCookieKey()}=${jwt}`
  });
};

// interface FileUploadAction {
//   file: File | Blob,
//   fileType: ImageType,
//   headers?: Record<string, string>
// }
// export const fileUploadAction = async ({
//   file,
//   fileType,
//   headers = {}
// }: FileUploadAction): Promise<{ fileURI: string }> => {
//   const url = new URL(`${apiURL}/api/file-upload`);
//   const fileExtension = fileType.replace(/^.*\//, "");
//   // Create FormData and append file
//   const formData = new FormData();
//   formData.append("image", file, `screenshot.${fileExtension}`);
//
//   const {
//     data, failure
//   } = await fetchJson(url, {
//     method: "POST",
//     body: formData,
//     headers: headers
//     // No need to set Content-Type header - it will be set automatically with boundary
//   });
//
//   if (failure) {
//     throw Logger.error({
//       message: "Failed to upload file for user",
//       error: failure.error,
//       metadata: failure.metadata
//     });
//   }
//
//   return data;
// };